import { lazy, StrictMode, Suspense } from "react";
import { Redirect } from "react-router";
import { BrowserRouter, Route } from "react-router-dom";
import { SWRConfig } from "swr";
import { NoticeProvider } from "./common/components/NoticeBanner/FullContext";
import Main from "./components/Main";
import Root from "./components/Root";
import EnvPatcher from "./components/TopBar/EnvPatcher";
import { PUBLIC_URL } from "./env";
import { WithErrorHandler } from "./errors";
import "./i18n";
import "./sentry";

const AppBar = lazy(() => import("./AppBar"));
const AppRoutes = lazy(() => import("./AppRoutes"));
const RedirectToPullRequestRoute = lazy(
    () => import("./RedirectToPullRequestRoute"),
);
const MobileMenu = lazy(() => import("./components/MobileMenu/MobileMenu"));
const TopBar = lazy(() => import("./components/TopBar/TopBar"));
const AccountIdChangedDispatcher = lazy(() =>
    import("./auth/useAccountId").then((module) => ({
        default: module.AccountIdChangedDispatcher,
    })),
);
const Allow = lazy(() => import("./auth/accessToken/components/Allow"));
const RedirectToAccount = lazy(
    () => import("./auth/accessToken/components/RedirectToAccount"),
);
const WithAccountAccessToken = lazy(
    () => import("./auth/accessToken/components/WithAccountAccessToken"),
);
const Welcome = lazy(() => import("./welcome/modal/components/Welcome"));
const CommandPalette = lazy(
    () => import("./components/CommandPalette/CommandPalette"),
);
const InstallShopify = lazy(() =>
    import("./callbacks/components/InstallShopify").then((module) => ({
        default: module.InstallShopify,
    })),
);
const PostHogWrapper = lazy(() =>
    import("./posthog/PostHogWrapper").then((module) => ({
        default: module.PostHogWrapper,
    })),
);

// Setting basename in BrowserRouter so that pull requests can be served from a relative path
const basename = PUBLIC_URL ? PUBLIC_URL : undefined;

function App() {
    return (
        <StrictMode>
            <Suspense fallback={null}>
                <SWRConfig
                    value={{
                        revalidateOnFocus: false,
                        revalidateOnReconnect: false,
                        revalidateOnMount: true,
                        shouldRetryOnError: false,
                        provider: () => new Map(),
                    }}
                >
                    <Root>
                        <Suspense fallback={null}>
                            <BrowserRouter basename={basename}>
                                <PostHogWrapper>
                                    <WithErrorHandler>
                                        <Route
                                            path={"/_pr"}
                                            component={
                                                RedirectToPullRequestRoute
                                            }
                                        />
                                        <Route
                                            path={"/"}
                                            exact
                                            component={RedirectToAccount}
                                        />
                                        <Route
                                            path={"/login"}
                                            render={() => <Redirect to="/" />}
                                        />
                                        <Route
                                            path="/shopify-install"
                                            exact
                                            component={InstallShopify}
                                        />
                                        <Route
                                            path={`/:accountId`}
                                            render={(props) => (
                                                <>
                                                    <AccountIdChangedDispatcher />
                                                    <WithAccountAccessToken
                                                        {...props}
                                                    >
                                                        <NoticeProvider>
                                                            <EnvPatcher>
                                                                <AppBar />
                                                                <TopBar />
                                                                <MobileMenu />
                                                                <Main>
                                                                    <Suspense
                                                                        fallback={
                                                                            null
                                                                        }
                                                                    >
                                                                        <AppRoutes />
                                                                    </Suspense>
                                                                    <Allow
                                                                        anyOfScopes={[
                                                                            [
                                                                                "admin:accounts",
                                                                            ],
                                                                        ]}
                                                                    >
                                                                        <Welcome />
                                                                    </Allow>
                                                                    <CommandPalette />
                                                                </Main>
                                                            </EnvPatcher>
                                                        </NoticeProvider>
                                                    </WithAccountAccessToken>
                                                </>
                                            )}
                                        />
                                    </WithErrorHandler>
                                </PostHogWrapper>
                            </BrowserRouter>
                        </Suspense>
                    </Root>
                </SWRConfig>
            </Suspense>
        </StrictMode>
    );
}

export default App;
